/**
 * Asset Loader — single entry point for fetching builder data assets.
 *
 * Phase 1a: helper exists and is unit-tested. No existing fetch('./data/...')
 * callsites are migrated yet. Phase 2 wires it through 14+ callsites and
 * gitignores the source dirs.
 *
 * Logical keys (e.g. 'data/section-index.json') are resolved against an
 * R2-served manifest (data-manifest.json) into hashed physical paths
 * (e.g. 'data/section-index.a3f0b7c2.json'). When the manifest is absent
 * (Phase 1a state) or the key is not in it, the helper falls back to the
 * logical key as the physical path so existing local-served assets work.
 *
 * For auth-gated assets, set { auth: true } and the request goes through
 * the Worker; otherwise it goes to the R2 public dev URL.
 */

import { reportError } from './report-error.js';

const MANIFEST_LOGICAL_KEY = 'data-manifest.json';

const _jsonCache = new Map(); // logicalKey -> Promise<T> (LRU-bounded, see below)
const _assetCache = new Map(); // logicalKey -> Promise<Response> (used internally)

// Phase 11: bound _jsonCache so browsing many templates/sections doesn't retain
// their parsed JSON forever. Map insertion order approximates recency (a hit
// re-inserts the key). Pinned boot/index files are reused constantly and never
// evicted. Evicting a resolved/in-flight promise is safe: a live awaiter still
// holds its reference; a later call simply re-fetches.
const JSON_CACHE_MAX = 24;
const JSON_CACHE_PINNED = new Set([
  'data/category-map.json',
  'data/section-index.json',
  'data/template-catalog-index.json'
]);

function jsonCacheGet(key) {
  if (!_jsonCache.has(key)) return undefined;
  const value = _jsonCache.get(key);
  // Refresh recency.
  _jsonCache.delete(key);
  _jsonCache.set(key, value);
  return value;
}

function jsonCacheSet(key, value) {
  _jsonCache.delete(key);
  _jsonCache.set(key, value);
  while (_jsonCache.size > JSON_CACHE_MAX) {
    let evicted = false;
    for (const candidate of _jsonCache.keys()) {
      if (JSON_CACHE_PINNED.has(candidate)) continue;
      _jsonCache.delete(candidate);
      evicted = true;
      break;
    }
    if (!evicted) break; // everything remaining is pinned
  }
}
let _manifestPromise = null;
let _manifestCached = null; // Phase 2.5: resolved Map for sync access (getAssetURLSync)

/**
 * Strip leading `./` from a logical key. Accepts both `data/foo.json` and
 * `./data/foo.json` for migration ergonomics.
 */
function normalizeKey(logicalKey) {
  if (typeof logicalKey !== 'string') {
    throw new TypeError('logicalKey must be a string');
  }
  if (logicalKey.startsWith('./')) return logicalKey.slice(2);
  return logicalKey;
}

/**
 * @returns {string} The R2 public base URL set in builder/index.html.
 */
export function getAssetBase() {
  const base = (typeof window !== 'undefined' && window.__PB_ASSET_BASE) || '';
  return base.replace(/\/+$/, '');
}

/**
 * @returns {string} The Worker origin (no path) derived from __PB_COMPOSE_ENDPOINT.
 *   E.g. 'https://page-builder-compose.josephclutts.workers.dev'
 */
export function getWorkerBase() {
  const endpoint = (typeof window !== 'undefined' && window.__PB_COMPOSE_ENDPOINT) || '';
  if (!endpoint) return '';
  try {
    const u = new URL(endpoint);
    return `${u.protocol}//${u.host}`;
  } catch {
    return '';
  }
}

function isLocalDevOrigin() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false;
  return ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(window.location.hostname);
}

/**
 * Build the physical URL for a logical key. The manifest may map the logical
 * key to a hashed physical name; when it doesn't, the logical key is used as-is.
 *
 * @param {string} logicalKey - e.g. 'data/section-index.json'
 * @param {object} [opts]
 * @param {boolean} [opts.auth=false] - if true, route through the Worker
 *   instead of the R2 public endpoint. Reserved for future auth-gated reads.
 * @param {Map<string,string>} [opts.manifest] - optional manifest override
 *   (testing). When omitted, the cached manifest from loadManifest() is used.
 * @returns {Promise<string>} the absolute URL to fetch.
 */
async function resolvePhysicalURL(logicalKey, opts = {}) {
  const key = normalizeKey(logicalKey);
  const manifest = opts.manifest || (await loadManifest());
  const manifestPhysical = manifest.get(key);
  const physical = manifestPhysical || key;
  const base = opts.auth ? getWorkerBase() + '/assets' : getAssetBase();
  if (!base) {
    // No __PB_ASSET_BASE set — fall back to same-origin relative fetch.
    // This preserves Phase 1a behavior (callsites still use ./data/foo).
    return `./${physical}`;
  }
  if (!opts.auth && !manifestPhysical && isLocalDevOrigin()) {
    return `./${key}`;
  }
  return `${base}/${physical}`;
}

function sameOriginFallbackURL(logicalKey, attemptedURL, opts = {}) {
  if (opts.auth) return '';
  const base = getAssetBase();
  if (!base) return '';
  if (typeof window === 'undefined' || !window.location?.href) return '';
  try {
    const attempted = new URL(attemptedURL, window.location.href);
    const current = new URL(window.location.href);
    if (attempted.origin === current.origin) return '';
  } catch {
    return '';
  }
  return `./${normalizeKey(logicalKey)}`;
}

// 15s per-attempt budget; AbortSignal.timeout is supported in all modern
// engines — when absent, fetches simply run without a deadline as before.
function _fetchTimeoutSignal() {
  try {
    return AbortSignal.timeout(15000);
  } catch {
    return undefined;
  }
}

async function fetchWithSameOriginFallback(logicalKey, url, opts = {}) {
  let resp;
  let fetchError = null;
  try {
    resp = await fetch(url, { signal: _fetchTimeoutSignal() });
  } catch (err) {
    fetchError = err;
  }

  if (resp?.ok) return resp;

  const fallbackURL = sameOriginFallbackURL(logicalKey, url, opts);
  if (fallbackURL) {
    try {
      const fallbackResp = await fetch(fallbackURL, { signal: _fetchTimeoutSignal() });
      if (fallbackResp.ok) return fallbackResp;
    } catch {
      // Expected-flow fallback: preserve the original R2/Worker failure below;
      // it has the useful URL.
    }
  }

  if (fetchError) throw fetchError;
  return resp;
}

/**
 * Fetch the manifest once and cache the result. Returns an empty Map if the
 * manifest is missing or unparseable so the helper degrades gracefully.
 *
 * @returns {Promise<Map<string,string>>}
 */
export function loadManifest() {
  if (_manifestPromise) return _manifestPromise;
  const base = getAssetBase();
  const url = base ? `${base}/${MANIFEST_LOGICAL_KEY}` : `./${MANIFEST_LOGICAL_KEY}`;
  _manifestPromise = (async () => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        _manifestCached = new Map();
        return _manifestCached;
      }
      const data = await resp.json();
      if (!data || typeof data !== 'object') {
        _manifestCached = new Map();
        return _manifestCached;
      }
      _manifestCached = new Map(Object.entries(data));
      return _manifestCached;
    } catch {
      // Missing/unparseable manifests are expected during local dev and stale R2
      // windows. Asset requests below report concrete failures.
      _manifestCached = new Map();
      return _manifestCached;
    }
  })();
  return _manifestPromise;
}

export function refreshManifest() {
  _manifestPromise = null;
  _manifestCached = null;
  return loadManifest();
}

export async function hasManifestAsset(logicalKey) {
  const key = normalizeKey(logicalKey);
  const manifest = await loadManifest();
  if (!manifest.size) return null;
  return manifest.has(key);
}

export function hasManifestAssetSync(logicalKey) {
  const key = normalizeKey(logicalKey);
  if (!_manifestCached?.size) return null;
  return _manifestCached.has(key);
}

/**
 * Synchronous URL resolver for callsites that can't await — `<img src=...>`,
 * inline CSS `url(...)`, etc. Reads from the cached manifest. If the manifest
 * hasn't been loaded yet, kicks off the async load (so subsequent calls hit
 * the cache) and falls back to `${base}/${logicalKey}` for this call.
 *
 * Phase 2.5: introduced for svg-library-utils.js which builds `<img src>`
 * URLs synchronously. Callers that can await should prefer `loadAsset` /
 * `loadJSON` for guaranteed manifest resolution.
 *
 * @param {string} logicalKey - e.g. 'svgs/foo.svg'
 * @returns {string} resolved URL
 */
export function getAssetURLSync(logicalKey) {
  const key = normalizeKey(logicalKey);
  // Trigger background load if not yet started; subsequent sync calls benefit.
  if (!_manifestPromise) loadManifest();
  const physical = (_manifestCached && _manifestCached.get(key)) || key;
  const base = getAssetBase();
  if (!base) return `./${physical}`;
  return `${base}/${physical}`;
}

/**
 * Fetch an asset by logical key. Returns the raw Response.
 *
 * Caches the in-flight Promise to dedupe simultaneous requests. The cached
 * value is never handed out directly; each caller receives a clone so body
 * readers like .text(), .json(), and .blob() remain safe across repeated calls.
 *
 * @param {string} logicalKey
 * @param {object} [opts]
 * @param {boolean} [opts.auth=false]
 * @param {boolean} [opts.bypass=false] - skip the in-memory cache
 * @returns {Promise<Response>}
 */
export async function loadAsset(logicalKey, opts = {}) {
  const key = normalizeKey(logicalKey);
  let promise = !opts.bypass ? _assetCache.get(key) : null;
  if (!promise) {
    promise = (async () => {
      const url = await resolvePhysicalURL(key, opts);
      const resp = await fetchWithSameOriginFallback(key, url, opts);
      if (!resp.ok) {
        throw new Error(`asset-loader: ${key} → ${url} returned ${resp.status}`);
      }
      return resp;
    })();
    if (!opts.bypass) _assetCache.set(key, promise);
    // On rejection, evict the cached failure so a retry can re-fetch.
    promise.catch((err) => {
      _assetCache.delete(key);
      reportError('AssetLoader', err, {
        user: 'silent',
        detail: `asset fetch failed for ${key}`
      });
    });
  }

  const resp = await promise;
  if (resp && typeof resp.clone === 'function') return resp.clone();
  // Test doubles may not implement clone(); production fetch Responses do.
  return resp;
}

/**
 * Fetch and JSON-parse an asset. Caches the parsed value (not the Response).
 *
 * @template T
 * @param {string} logicalKey
 * @param {object} [opts]
 * @param {boolean} [opts.auth=false]
 * @param {boolean} [opts.bypass=false]
 * @returns {Promise<T>}
 */
export async function loadJSON(logicalKey, opts = {}) {
  const key = normalizeKey(logicalKey);
  if (!opts.bypass) {
    const cached = jsonCacheGet(key);
    if (cached !== undefined) return cached;
  }
  const promise = (async () => {
    const url = await resolvePhysicalURL(key, opts);
    const resp = await fetchWithSameOriginFallback(key, url, opts);
    if (!resp.ok) {
      throw new Error(`asset-loader: ${key} → ${url} returned ${resp.status}`);
    }
    return resp.json();
  })();
  if (!opts.bypass) jsonCacheSet(key, promise);
  promise.catch((err) => {
    _jsonCache.delete(key);
    reportError('AssetLoader', err, {
      user: 'silent',
      detail: `JSON fetch failed for ${key}`
    });
  });
  return promise;
}

/**
 * Test/dev hook to clear all caches. Not exported as a public API; tests
 * import it via name. Production code should never call this.
 */
export function _resetCachesForTesting() {
  _jsonCache.clear();
  _assetCache.clear();
  _manifestPromise = null;
  _manifestCached = null;
}

/** Phase 11: introspect the JSON LRU (tests only). */
export function _jsonCacheInfoForTesting() {
  return {
    size: _jsonCache.size,
    keys: [..._jsonCache.keys()],
    max: JSON_CACHE_MAX,
    pinned: [...JSON_CACHE_PINNED]
  };
}
